import { cloneDeep, get, set, unset } from "lodash-es";

import { Operation, Revision } from "../types/schemas";
import { APITranscript } from "../types/types";
import { assertNever } from "./assert-never";

function applyObjectSet(
  { path, value }: Extract<Operation, { type: "object-set" }>,
  transcript: APITranscript,
): APITranscript {
  if (path.length === 0) return cloneDeep(value);
  set(transcript, path, cloneDeep(value));
  return transcript;
}

function applyObjectDelete(
  { path }: Extract<Operation, { type: "object-delete" }>,
  transcript: APITranscript,
): APITranscript {
  unset(transcript, path);
  return transcript;
}

function applyArrayInsert(
  { path, index, values }: Extract<Operation, { type: "array-insert" }>,
  transcript: APITranscript,
): APITranscript {
  const arr = get(transcript, path);
  if (index > arr.length || index < 0)
    throw new Error(
      `array-insert: index ${index} out of bounds for array length ${arr.length} for path ${path.join(",")}`,
    );

  arr.splice(index, 0, ...cloneDeep(values));
  return transcript;
}

function applyArrayDelete(
  { path, startIndex, endIndex }: Extract<Operation, { type: "array-delete" }>,
  transcript: APITranscript,
): APITranscript {
  const arr = get(transcript, path);
  if (startIndex > endIndex)
    throw new Error(
      `array-delete: startIndex (${startIndex}) must be less than or equal to endIndex (${endIndex}) for path ${path.join(",")}`,
    );
  if (
    startIndex > arr.length ||
    startIndex < 0 ||
    endIndex > arr.length ||
    endIndex < 0
  )
    throw new Error(
      `array-delete: startIndex ${startIndex} or endIndex ${endIndex} out of bounds for array length ${arr.length} for path ${path.join(",")}`,
    );

  arr.splice(startIndex, endIndex - startIndex + 1);
  return transcript;
}

/** Applies the operation, mutating the transcript. */
export function applyOperation(
  operation: Operation | null,
  transcript: APITranscript,
): APITranscript {
  if (!operation) return transcript;

  switch (operation.type) {
    case "object-set":
      return applyObjectSet(operation, transcript);
    case "object-delete":
      return applyObjectDelete(operation, transcript);
    case "array-insert":
      return applyArrayInsert(operation, transcript);
    case "array-delete":
      return applyArrayDelete(operation, transcript);
    default:
      assertNever(operation, "Unhandled operation type");
  }

  return transcript;
}

/** Applies the revision, mutating the transcript. */
export function applyRevision(
  revision: Revision | null,
  transcript: APITranscript,
): APITranscript {
  if (!revision) return transcript;
  return revision.operations.reduce(
    (acc, operation) => applyOperation(operation, acc),
    transcript,
  );
}
